import type { TFunction } from 'i18next';
import type { MenuPreviewChange, MenuPreviewWarning } from '../menuGovernanceApi';

/**
 * Rebuilds a preview warning's sentence from `code` plus the change's own
 * structured fields, in the venue's own language.
 *
 * `publish-preview.ts` builds `warning.message` as a plain English sentence
 * with no i18n on the backend at all — see the doc comment on
 * {@link MenuPreviewWarning.message}. That string is never rendered. Every one
 * of the four warning codes is reconstructable without it, because
 * `MenuChange` already carries `itemName`, `branchName` and `pricePercent` as
 * their own fields:
 *
 *  - `MISSING_TRANSLATION` loses only *which* language is missing — the server
 *    computes that from `localesAfter`, which this payload does not carry.
 *    Naming the item and branch without naming the language is still an
 *    accurate, actionable sentence; it is not the identical one.
 *  - `LARGE_PRICE_MOVE`, `WITHDRAWN_WHILE_LISTED` and `PRICED_TO_ZERO` are
 *    fully reconstructable — nothing is lost.
 */
export function warningSentence(
  warning: MenuPreviewWarning,
  change: MenuPreviewChange,
  t: TFunction,
): string {
  switch (warning.code) {
    case 'MISSING_TRANSLATION':
      return t('menuGovernance.preview.warningMissingTranslation', {
        item: change.itemName,
        branch: change.branchName,
      });
    case 'LARGE_PRICE_MOVE':
      return t('menuGovernance.preview.warningLargePriceMove', {
        item: change.itemName,
        branch: change.branchName,
        percent: percentMagnitude(change.pricePercent),
        direction: t(
          priceMoveDirection(change.pricePercent) === 'down'
            ? 'menuGovernance.preview.directionDown'
            : 'menuGovernance.preview.directionUp',
        ),
      });
    case 'WITHDRAWN_WHILE_LISTED':
      return t('menuGovernance.preview.warningWithdrawnWhileListed', {
        item: change.itemName,
        branch: change.branchName,
      });
    case 'PRICED_TO_ZERO':
      return t('menuGovernance.preview.warningPricedToZero', {
        item: change.itemName,
        branch: change.branchName,
      });
    default:
      return warning.code;
  }
}

/** `"-40.00"` → `"40.00"` — the sentence already says "up"/"down" via the sign. */
function percentMagnitude(percent: string | null): string {
  if (!percent) return '0';
  return percent.startsWith('-') ? percent.slice(1) : percent;
}

/** Up, down, or unchanged — for an arrow/tone rather than the raw sign. */
export function priceMoveDirection(percent: string | null): 'up' | 'down' | 'none' {
  if (!percent) return 'none';
  const value = Number(percent);
  if (!Number.isFinite(value) || value === 0) return 'none';
  return value > 0 ? 'up' : 'down';
}
